/* eslint-disable react-refresh/only-export-components -- context file */
import React from 'react';
import { useEffect, useState, useCallback, useRef } from 'react';
import { UseStateError } from 'Utils/Errors';
import { RepertoireReducer } from 'Types/RepertoireTypes';
import { useDispatch, useSelector } from 'react-redux';
import { IntervalTimer } from 'Utils/IntervalTimer';
import { createTimeKeeper } from 'Models/createTimeKeeper';
import EventStream from 'Models/EventStream';
import { TimingOffsetsConfig } from 'Models/EventStream';
import { MainAppReducer } from 'Types';
import MusicXML from 'Models/MusicXML';
import Phrase from 'Models/Phrase';
import * as appActions from 'Actions/app';
import * as eventActions from 'Actions/events';
import TimeSignature from 'Models/TimeSignature';
import { MusicXMLStream } from 'Models/MusicXMLStream';
import { List } from 'immutable';
import Scheduler2 from 'Utils/Scheduler';
import { NoteType } from '@museflow-ai/opensheetmusicdisplay';
import { divisionsPerMeasureToNoteTypeIn4Over4 } from 'Utils';
import { TickNoteEvent } from 'Utils/CustomEvents';
import { REPERTOIRE_PROGRESS } from 'Types/RepertoireTypes';
import * as repertoireActions from '../Actions/repertoire';
import { find } from 'lodash';
import axios, { AxiosError } from 'axios';
import { useMidiContext, type NoteMessage } from 'Contexts/MidiContext';
import logger from 'Utils/Logger';
import { awsRum } from 'Utils/AwsRum';
import { getCurrentAuthToken } from 'Utils/Auth';
import { useAuthWrapperContext } from './AuthWrapperContext';
import { useWakeLock } from 'Hooks/useWakeLock';
import { NotifyPhaser, MidiOn, MidiOff } from 'Phaser/EventBus';

export enum AuthStatus {
  UNAUTHENTICATED,
  PENDING,
  ERROR,
  SUCCESS,
}

export enum UserActionTypeEnum {
  LOG_IN,
  LOG_OUT,
  SIGN_UP,
}

export type RepertoireStateType = {
  allowChevrons: boolean;
  accuracyPercent: number | null | undefined;
  performanceTempo: number | null;
  inPerformanceMode: boolean;
  scheduler2: Scheduler2 | null;
  midiStream: EventStream;
  topPhraseStartTimestamp: number | null;
  bottomPhraseStartTimestamp: number | null;
  musicLoaded: boolean;
  phraseParts: Phrase[][];
  updateKey: number;
  showCursorSelector: boolean;
  isPlaying: boolean;
  deferSchedulerStart: boolean;
  localPlaySessionId: string | undefined | null;
};

export type RepertoireFunctions = {
  setAccuracy: (accuracy: number) => void;
  setAllowChevrons: React.Dispatch<React.SetStateAction<boolean>>;
  setMusicLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  allPhrasesLoaded: () => boolean;
  setInPerformanceMode: (prev: boolean | ((prev: boolean) => boolean)) => void;
  createScheduler: (opts: unknown) => Promise<void>;
  setShowCursorSelector: React.Dispatch<React.SetStateAction<boolean>>;
  setIsPlaying: (isPlaying: boolean) => void;
  softRefreshRepertoire: () => Promise<void>;
  setDeferSchedulerStart: (defer: boolean) => void;
  startScheduler: (measureOffset?: number, tempoOverride?: number) => Promise<void>;
  setForwardMidiToPhaser: (forward: boolean) => void;
  setSkipContextEventDispatching: (skip: boolean) => void;
};

const initialState = {
  allowChevrons: false, //undefined,
  accuracyPercent: 0, //undefined,
  scheduler2: undefined,
  midiStream: undefined,
  topPhraseStartTimestamp: undefined,
  bottomPhraseStartTimestamp: undefined,
  musicLoaded: undefined,
  phraseParts: undefined,
  updateKey: undefined,
  showCursorSelector: false,
  performanceTempo: null,
  inPerformanceMode: false,
  isPlaying: false,
  deferSchedulerStart: false,
  localPlaySessionId: undefined as string | undefined | null,
};

/* eslint-disable react-hooks/rules-of-hooks -- hooks run in sync path; async is for return type */
async function useComponentWillUnmount(cleanupCallback = () => {}) {
  const callbackRef = useRef(cleanupCallback);
  callbackRef.current = cleanupCallback; // always up to date
  useEffect(() => {
    return () => {
      callbackRef.current();
    };
  }, []);
}
/* eslint-enable react-hooks/rules-of-hooks */

export const RepertoirePlayContext = React.createContext<
  (RepertoireStateType & RepertoireFunctions) | undefined
>(undefined);

export const useRepertoirePlayContext: () => RepertoireStateType & RepertoireFunctions = () => {
  const state = React.useContext(RepertoirePlayContext);
  if (!state) {
    throw new UseStateError(useRepertoirePlayContext, RepertoirePlayProvider);
  }

  return {
    ...state,
  };
};

export type RepertoirePlayProviderProps = {
  children?: React.ReactNode;
};

interface PhraseItem {
  s3_music_xml_id: string;
  s3_music_xml_url: string;
  music_xml_url_pointers: {
    [key: string]: string;
  };
  time_signature_numerator: number;
  time_signature_denominator: number;
  num_notes: number;
}
const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/`;

async function postSession(jwtToken: string, body: { repertoire: { id: number } }) {
  return axios.post(baseUrl + 'rep-play-session', body, {
    headers: { Authorization: `Bearer ${jwtToken}` },
  });
}

export const RepertoirePlayProvider: React.FC<RepertoirePlayProviderProps> = ({
  children,
}: RepertoirePlayProviderProps) => {
  // logger.info("Number of times context loading?")
  const [state, _setState] = React.useState<RepertoireStateType>(initialState);
  const data = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const auth = useAuthWrapperContext();
  const { errorRecognitionActive } = data;
  const { userData } = data;
  const repertoireReducerData = useSelector((state: RepertoireReducer) => state.repertoireReducer);
  const userRepertoireProgress = repertoireReducerData.userRepertoireProgresses;
  const repertoireData = repertoireReducerData.repertoireData;
  const repertoireIdSelect = repertoireReducerData.repertoireIdSelect;
  const playSessionId = repertoireReducerData.playSessionId;
  const repertoireProgressSelected = find(
    userRepertoireProgress,
    (urd) => urd?.repertoire?.id === repertoireIdSelect,
  );
  const repertoireSelected = find(repertoireData, (rd) => rd.id === repertoireIdSelect);
  const performanceTempo = repertoireSelected?.bpm || 72;
  const [musicXMLStream] = useState(new MusicXMLStream());
  const tempo = repertoireProgressSelected?.last_tempo || performanceTempo;
  const { addListener, removeListener, clearListeners } = useMidiContext();

  //const repertoireSelected = find(repertoireData, rd => rd.id === repertoireIdSelect)
  const userRepertoireSelected = find(userRepertoireProgress, (rd) => {
    if (rd == null) return false;
    if (rd.repertoire) {
      return rd.repertoire?.id === repertoireIdSelect;
    } else {
      return false;
    }
  });
  const [allowChevrons, setAllowChevrons] = useState(false);
  const [measuresPerPhrase, setMeasuresPerPhrase] = useState(5); //4)
  const musicXmlSvgWidth = useRef(960);
  const minTempoForChevronFlash = 72;
  const dispatch = useDispatch();
  const recordPlayTimeIntervalTimer = React.useRef<IntervalTimer | undefined>(undefined);
  const sendPlayTimeIntervalTimer = React.useRef<IntervalTimer | undefined>(undefined);
  const lastPlayedNoteTimer = React.useRef<NodeJS.Timeout | undefined>(undefined);
  const accumulatedTimeRef = React.useRef(0);
  const lastMeasuredTimeRef = React.useRef(0);

  const [timeKeeper] = useState(createTimeKeeper());

  const [midiStream] = useState(
    new EventStream(
      timeKeeper,
      new TimingOffsetsConfig(),
      data.userData?.colorblind_type || null,
      1,
      0.025,
      0.1,
      true,
    ),
  );
  const allowRepeatPhrases = false;
  /// This should be in redux instead??
  const [_showAccuracy, setShowAccuracy] = useState(false);
  const [phraseParts, setPhraseParts] = useState<List<Phrase | undefined>>(List([]));
  const [musicLoaded, setMusicLoaded] = useState(false);
  const [started, setStarted] = useState(false);
  const [topPhraseStartTimestamp, setTopPhraseStartTimestamp] = useState(100000);
  const [bottomPhraseStartTimestamp, setBottomPhraseStartTimestamp] = useState(100000);
  const [tickNoteType] = useState(NoteType._64th);
  const phrasePartsRef = useRef<List<Phrase | undefined>>(phraseParts);
  const [updateKey, setUpdateKey] = useState(0);

  // Refs to store pending timestamp values during soft refresh
  // These are applied after unpauseTicks completes in onStart
  const pendingTopTimestampRef = useRef<number | null>(null);
  const pendingBottomTimestampRef = useRef<number | null>(null);
  const [deferSchedulerStart, setDeferSchedulerStart] = useState(false);
  // When true, the OSMD container handles event dispatching (RepertoirePlayingEvent,
  // RepertoireCompletedEvent) using osmdEventStream. The context skips its own
  // sendPlayTimeInterval and onCalcTheEnd dispatches to avoid sending events
  // with null accuracy (midiStream has zero events in OSMD mode).
  // Same pattern as forwardMidiToPhaserRef below.
  const skipContextEventDispatchingRef = useRef(false);
  const setSkipContextEventDispatching = useCallback((skip: boolean) => {
    skipContextEventDispatchingRef.current = skip;
  }, []);

  // When true, the context's MIDI listener also forwards events to the
  // Phaser EventBus via NotifyPhaser(MidiOn/MidiOff). Set by the OSMD
  // repertoire container so that MIDI events survive clearListeners() and
  // always reach the Phaser scene. Uses a ref (not state) because the
  // noteOn/noteOff callbacks capture it via closure through listener.current.
  const forwardMidiToPhaserRef = useRef(false);
  const setForwardMidiToPhaser = useCallback((forward: boolean) => {
    forwardMidiToPhaserRef.current = forward;
  }, []);
  // Stores the unpauseTicks arguments when deferSchedulerStart is true,
  // so startScheduler() can call unpauseTicks later (on first MIDI note).
  const deferredUnpauseArgsRef = useRef<{
    tempo: number;
    tickMultiplier: number;
    lastPlayedMeasure: number;
    tickNoteType: NoteType;
  } | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [accuracyPercent, setAccuracyPercent] = useState<number | null>(null);
  const [allPhrasesLoaded, setAllPhrasesLoaded] = useState(false);
  const [inPerformanceMode, setInPerformanceMode] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const [showCursorSelector, setShowCursorSelector] = useState(false);

  const [accuracyAtTempo, setAccuracyAtTempo] = useState<number[]>([]);
  const [localPlaySessionId, setLocalPlaySessionId] = React.useState<string | undefined | null>(
    undefined,
  );

  const setAccuracy = (accuracy: number | null) => {
    setAccuracyPercent(accuracy);
  };

  const recordPlayTimeInterval = React.useCallback(() => {
    if (repertoireIdSelect !== undefined && localPlaySessionId) {
      const now = new Date().getTime();
      accumulatedTimeRef.current = now - lastMeasuredTimeRef.current + accumulatedTimeRef.current;
      lastMeasuredTimeRef.current = now;
    }
  }, [repertoireIdSelect, localPlaySessionId]);

  useWakeLock();

  // Last measure that the user fully played.
  const lastPlayedMeasureRef = useRef(-1);
  // Last updated phrase index 0-3
  const lastUpdatedIndexRef = useRef(-1);
  // What measure offset should the next phrase have
  const nextPhraseStartTimestampRef = useRef(0);
  // When we reach this measure, we should grab and update the next new phrase.
  // Subtract one here since we really want to switch at measure 6, but we use 0-based indexing.
  const nextPhraseUpdateMeasureRef = useRef(measuresPerPhrase * 1.5 - 1);
  const nextPhraseStartMeasureRef = useRef(0);
  // This interval timer isn't related to the in-game audiocontext timer.
  // it doesn't have to be as precise. Also, the scheduler only schedules in ticks.

  const keyCallbackRef = useRef<((ev: KeyboardEvent) => void) | null>(null);

  // useEffect(()=> {
  //   midiStream.errorRecognitionActive = !inPerformanceMode
  //   midiStream.clearErrors()
  // }, [inPerformanceMode])

  useEffect(() => {
    midiStream.setErrorRecognitionActive(errorRecognitionActive);
  }, [errorRecognitionActive]);

  const noteOff = useCallback(
    (ev: NoteMessage) => {
      midiStream.handleNoteOffEvent(ev.note);
      // Forward to Phaser scene when OSMD repertoire is active
      if (forwardMidiToPhaserRef.current) {
        logger.debug(
          '[OSMD-MIDI] Context noteOff forwarding to Phaser, note:',
          ev.note,
          'midiStream.id:',
          midiStream.id,
        );
        NotifyPhaser(MidiOff(ev.note));
      }
    },
    [midiStream],
  );

  const noteOn = useCallback(
    (ev: NoteMessage) => {
      if (ev.velocity !== 0) {
        logger.debug(
          '[OSMD-MIDI] Context noteOn, note:',
          ev.note,
          'velocity:',
          ev.velocity,
          'forwardToPhaser:',
          forwardMidiToPhaserRef.current,
          'midiStream.id:',
          midiStream.id,
        );
        setShowAccuracy(true);
        clearTimeout(lastPlayedNoteTimer?.current);
        if (process.env.REACT_APP_CURSOR_TIMEOUT) {
          lastPlayedNoteTimer.current = setInterval(() => {
            dispatch(repertoireActions.setRepertoirePlaying({ repertoirePlaying: false }));
            if (localPlaySessionId && repertoireReducerData.repertoirePlaying) {
              logger.info(1);
              dispatch(
                eventActions.repertoirePauseEventAction(
                  repertoireIdSelect,
                  localPlaySessionId,
                ) as unknown,
              );
            }
          }, 10000);
        }
        /* eslint-disable-next-line no-constant-condition -- inPerformanceMode check placeholder */
        if (true) {
          //!inPerformanceMode) {
          midiStream.handleNoteOnEvent(ev.note, ev.velocity);
        }
        // Forward to Phaser scene when OSMD repertoire is active
        if (forwardMidiToPhaserRef.current) {
          logger.debug('[OSMD-MIDI] Context noteOn forwarding to Phaser via NotifyPhaser(MidiOn)');
          NotifyPhaser(MidiOn(ev.note, ev.velocity));
        }
      } else {
        noteOff(ev); // call the note off logic becasue velocity is 0.
      }
    },
    [midiStream, noteOff, repertoireReducerData.repertoirePlaying],
  ); //, inPerformanceMode]);

  const listener = React.useRef({
    name: 'eventListener',
    noteOn,
    noteOff,
  });

  const [scheduler2, setScheduler2] = useState<Scheduler2 | null>(null);

  useEffect(() => {
    const firstPhrase = phrasePartsRef.current.get(0);
    if (allPhrasesLoaded && firstPhrase) {
      const countInMeasures =
        (firstPhrase.getTimeSigFract() + firstPhrase.getPickUpMeasureOffset()) *
        (1 / firstPhrase.getTimeSigFract());
      scheduler2?.destroy();
      setScheduler2(null);
      const newScheduler = new Scheduler2(
        timeKeeper,
        -countInMeasures,
        firstPhrase.getPickUpMeasureOffset(),
      );
      setScheduler2(newScheduler);

      return () => {
        newScheduler.destroy();
        setScheduler2(null);
      };
    }
  }, [allPhrasesLoaded]);

  const allPhrasesLoadedRef = useRef(allPhrasesLoaded);
  useEffect(() => {
    allPhrasesLoadedRef.current = allPhrasesLoaded;
  }, [allPhrasesLoaded]);

  const createScheduler = useCallback(() => {
    const firstPhrase = phrasePartsRef.current.get(0);
    console.log('HERE doing it?', allPhrasesLoadedRef.current, firstPhrase);
    if (!allPhrasesLoadedRef.current || !firstPhrase) return null;

    const countInMeasures =
      (firstPhrase.getTimeSigFract() + firstPhrase.getPickUpMeasureOffset()) *
      (1 / firstPhrase.getTimeSigFract());

    const newScheduler = new Scheduler2(
      timeKeeper,
      -countInMeasures,
      firstPhrase.getPickUpMeasureOffset(),
    );
    console.log('SEt the scheduler yeah?', newScheduler);
    setScheduler2(newScheduler);

    return newScheduler;
  }, [phrasePartsRef, timeKeeper]);

  const resetScheduler = useCallback(() => {
    scheduler2?.destroy();
    const scheduler = new Scheduler2(timeKeeper);
    setScheduler2(scheduler);

    return () => {
      scheduler.destroy();
      setScheduler2(null);
    };
  }, [scheduler2, timeKeeper]);

  useEffect(() => {
    // Set the metronome from state. (Will apply for initial state as well as when user changes preference since reducer will trigger this.)
    // Default to true if undefined
    const metronomeSound = userData?.rep_metronome_sound ?? true;
    scheduler2?.changeMetronomeAudio(metronomeSound);
  }, [scheduler2, userData?.rep_metronome_sound]);

  useEffect(() => {
    // Set the downbeats from state. (Will apply for initial state as well as when user changes preference since reducer will trigger this.)
    // Default to true if undefined
    const downbeatsSound = userData?.rep_downbeats_sound ?? true;
    scheduler2?.changeDownbeatsAudio(downbeatsSound);
  }, [scheduler2, userData?.rep_downbeats_sound]);

  const getPhraseInfo = useCallback(async () => {
    const options = {
      url: process.env.REACT_APP_BACKEND_URL + `/api/v1/phrases?repertoireId=${repertoireIdSelect}`,
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await auth.currentAuthToken()}`,
      },
    };
    const response = await axios.request(options);
    // Sort the response based on order_number
    const phrase_items = (response as { data: { data: PhraseItem[] } }).data.data.sort(
      (p1: { order_number: number }, p2: { order_number: number }) =>
        p1.order_number - p2.order_number,
    );
    const phraseArr = phrase_items.map((phrase_item: PhraseItem) => {
      return {
        uuid: phrase_item['s3_music_xml_id'],
        s3_music_xml_url: phrase_item['s3_music_xml_url'],
        svg_url: phrase_item['music_xml_url_pointers'][`svg_url_${musicXmlSvgWidth.current}`],
        svg_url_timesig:
          phrase_item['music_xml_url_pointers'][`svg_url_timesig_${musicXmlSvgWidth.current}`],
        json_data_url:
          phrase_item['music_xml_url_pointers'][`json_data_url_${musicXmlSvgWidth.current}`],
        json_data_url_timesig:
          phrase_item['music_xml_url_pointers'][
            `timesig_json_data_url_${musicXmlSvgWidth.current}`
          ],
        time_signature_numerator: phrase_item['time_signature_numerator'],
        time_signature_denominator: phrase_item['time_signature_denominator'],
        num_notes: phrase_item['num_notes'],
      };
    });
    return phraseArr;
  }, [
    data.levelSelect,
    data.levelData,
    auth.currentAuthToken,
    musicXmlSvgWidth,
    repertoireIdSelect,
  ]);

  const getNextPhrase = useCallback(
    async function genNextPhrase() {
      const phraseDataArr = await getPhraseInfo();
      const phrasePromises = phraseDataArr.map(async (phraseData: Record<string, unknown>) => {
        const [musicXmlRes, cursorDataRes, cursorDataTimesigRes] = await Promise.all([
          axios.get(phraseData.s3_music_xml_url),
          axios.get(phraseData.json_data_url),
          axios.get(phraseData.json_data_url_timesig),
        ]);

        const musicXML = new MusicXML(musicXmlRes.data, [
          new TimeSignature(
            phraseData.time_signature_numerator,
            phraseData.time_signature_denominator,
          ),
        ]);
        const phrase = new Phrase(
          phraseData.uuid,
          musicXML,
          phraseData.num_notes,
          phraseData.svg_url,
          phraseData.svg_url_timesig,
          cursorDataRes.data.serializedIterator,
          cursorDataTimesigRes.data.serializedIterator,
          cursorDataRes.data.measureRects,
        );
        return phrase;
      });
      // phrasePromises = phrasePromises.slice(2,)  // <- Test 5 measures in phrase Good King Wenceslas
      return await Promise.all(phrasePromises);
    },
    [getPhraseInfo],
  );

  const updatePhraseParts = useCallback(
    function updatePhraseParts(
      newPhrasePart: Phrase | undefined,
      totalNumPhrases: number,
      varmeasuresPerPhrase: number,
      firstPhrasePickUpMeasureOffset: number,
    ) {
      const nextIndexRef = (lastUpdatedIndexRef.current + 1) % totalNumPhrases; //varmeasuresPerPhrase//4
      if (nextIndexRef % 2 == 0) {
        setTopPhraseStartTimestamp(nextPhraseStartTimestampRef.current);
      } else {
        setBottomPhraseStartTimestamp(nextPhraseStartTimestampRef.current);
      }
      if (nextIndexRef !== 0) {
        newPhrasePart?.setStartTimestamp(
          nextPhraseStartTimestampRef.current - firstPhrasePickUpMeasureOffset,
        );
      } else {
        newPhrasePart?.setStartTimestamp(nextPhraseStartTimestampRef.current);
      }
      timeKeeper.setTimeSignatures(
        nextPhraseStartMeasureRef.current,
        newPhrasePart?.musicXML?.timeSignatures ?? [],
      );

      nextPhraseStartTimestampRef.current += varmeasuresPerPhrase * timeKeeper.measureDurationAt(0);
      nextPhraseStartMeasureRef.current += varmeasuresPerPhrase;

      phrasePartsRef.current = phrasePartsRef.current.set(nextIndexRef, newPhrasePart);

      setPhraseParts(phrasePartsRef.current);
      lastUpdatedIndexRef.current = nextIndexRef;
    },
    [
      measuresPerPhrase,
      nextPhraseStartTimestampRef.current,
      nextPhraseStartMeasureRef.current,
      phrasePartsRef.current,
      lastUpdatedIndexRef.current,
    ],
  );

  const getTiersForLevel = useCallback(
    async function getTiersForLevel() {
      const levelNumber = data.levelData[data.levelSelect].level_number;
      const options = {
        url:
          process.env.REACT_APP_BACKEND_URL +
          `/api/v1/phrases/tiers-by-level?levelNumber=${levelNumber}`,
        headers: {
          'Access-Control-Allow-Origin': '*',
          Authorization: `Bearer ${await auth.currentAuthToken()}`,
        },
      };
      const response = await axios.request(options);
      return response['data'];
    },
    [data.levelSelect, data.levelData, auth.currentAuthToken],
  );

  const getNextNMeasures = useCallback(async () => {
    try {
      const newPhrases = await getNextPhrase();
      newPhrases.forEach((phrase) => {
        musicXMLStream.addPhrase(phrase);
      });
      // Fetch the next phrase from the stream
      setMeasuresPerPhrase(newPhrases.length);
      newPhrases.forEach(() => {
        const nextPhrase = musicXMLStream.nextPhrase();
        updatePhraseParts(
          nextPhrase,
          newPhrases.length,
          nextPhrase!.musicXML?.measures?.length,
          newPhrases[0].pickUpMeasureOffset,
        );
      });
    } catch (error) {
      console.error('Error fetching measures:', error);
    }
  }, [getNextPhrase, updatePhraseParts, musicXMLStream, allowRepeatPhrases, phraseParts]);

  const calcCompletion = useCallback(
    (accuracyAtTempoLocal: number[]) => {
      let completeSum = 0;
      accuracyAtTempoLocal.forEach((el) => (completeSum += el));
      let percentComplete =
        midiStream.totalNotesLength() > 0 ? completeSum / midiStream.totalNotesLength() : 0; // make sure not dividing by 0
      if (percentComplete >= 1.0) {
        percentComplete = 1;
      } else if (percentComplete < 0) {
        percentComplete = 0;
      }
      return percentComplete;
    },
    [midiStream],
  );

  // by breaking up the recording of play time and the sending of play time we can be more accurate
  // with the recording without ddosing the backend.
  const sendPlayTimeInterval = React.useCallback(() => {
    // When the OSMD container is active, it dispatches RepertoirePlayingEvent
    // using osmdEventStream (which has the events). Skip the context's own
    // dispatch because midiStream has zero events in OSMD mode and
    // calcRepAccuracy() returns NaN → null in JSON.
    if (skipContextEventDispatchingRef.current) {
      logger.info('[ACC-TRACE] CONTEXT sendPlayTimeInterval SKIPPED (OSMD mode)');
      return;
    }

    if (repertoireIdSelect !== undefined && localPlaySessionId) {
      const [accuracy, accuracyAtTempo] = midiStream.calcRepAccuracy(tempo, performanceTempo);
      const completion = calcCompletion(accuracyAtTempo) * 100;
      logger.info('[ACC-TRACE] CONTEXT sendPlayTimeInterval DISPATCHING', {
        source: 'context-interval',
        accuracy,
        isNaN_accuracy: isNaN(accuracy),
        typeof_accuracy: typeof accuracy,
        completion,
        midiStreamId: midiStream.id,
        accuracyQueueLength: midiStream.accuracyQueueLength(),
        totalNotesLength: midiStream.totalNotesLength(),
        repertoireIdSelect,
        localPlaySessionId,
      });
      dispatch(
        eventActions.repertoirePlayingEventAction(
          repertoireIdSelect,
          localPlaySessionId,
          accumulatedTimeRef.current,
          completion,
          accuracy,
        ) as unknown,
      );
    }
  }, [repertoireIdSelect, localPlaySessionId, accuracyAtTempo, calcCompletion]);

  useEffect(() => {
    if (musicLoaded && !repertoireReducerData.repertoirePlaying) {
      // When the OSMD container is active, it dispatches
      // updateRepertoireDataRequest using osmdEventStream (which has the
      // correct events and accuracy). Skip the context's own dispatch because
      // midiStream uses VexFlow iterators that may produce different event
      // counts/timestamps, resulting in inaccurate percent_complete values.
      if (skipContextEventDispatchingRef.current) {
        logger.info('[ACC-TRACE] CONTEXT progress update SKIPPED (OSMD mode)');
        return;
      }

      let repertoireStatus = REPERTOIRE_PROGRESS.IN_PROGRESS;
      const [, accuracyAtTempo] = midiStream.calcRepAccuracy(tempo, performanceTempo);
      const percentComplete = calcCompletion(accuracyAtTempo);
      if (percentComplete >= 1.0) {
        repertoireStatus = REPERTOIRE_PROGRESS.COMPLETE;
      }
      let bestAccuracy;
      let bestPercentComplete;
      if (
        userRepertoireSelected &&
        userRepertoireSelected.percent_complete &&
        100 * percentComplete < userRepertoireSelected.percent_complete
      ) {
        // Use best last values for accuracy and percent complete
        bestAccuracy = userRepertoireSelected.accuracy;
        bestPercentComplete = userRepertoireSelected.percent_complete;
      } else {
        bestAccuracy = accuracyPercent || 0;
        bestPercentComplete = 100 * percentComplete;
      }
      const data = {
        status: repertoireStatus,
        repertoire: { id: repertoireIdSelect },
        last_played_at: new Date().toISOString(),
        completed_at: null,
        accuracy: bestAccuracy,
        percent_complete: bestPercentComplete,
        last_tempo: tempo,
      };
      logger.info('updating data!', data);
      dispatch(repertoireActions.updateRepertoireDataRequest({ ...data }));
      // dispatch(eventActions.repertoireAttemptEventAction(repertoireIdSelect, ))
    }
  }, [auth.currentAuthToken, repertoireReducerData.repertoirePlaying, musicLoaded]);

  const dispatchPhraseEvent = React.useCallback(
    function dispatchPhraseEvent(
      repertoireId: number,
      playSessionId: string,
      nBeforPhrase: number = 1,
    ) {
      const phrase = midiStream.getNBeforePhrase(nBeforPhrase);
      // delay one quarter note to make sure we get the offset tick of last note in phrase
      if (phrase && !phrase?.getComplete() && localPlaySessionId) {
        // run 1 second after to capture the last note, but set complete before running so that
        // this doesn't happen again during the 1 second wait.
        phrase?.setComplete(true);
        setTimeout(() => {
          // Check if phrase is still marked as complete - a soft reset would have cleared this
          // This prevents dispatching stale data from a previous playthrough
          if (phrase && phrase.getComplete()) {
            const phraseData = phrase.generateRepPhraseCompletedEventData();
            dispatch(
              eventActions.repPhraseCompletedEventAction({
                midiData: [],
                errorData: phraseData.errorData,
                accuracy: phraseData.accuracy,
                lHAccuracy: phraseData.lhAccuracy,
                rHAccuracy: phraseData.rhAccuracy,
                totalNotes: phraseData.totalNotes,
                lhTotalNotes: phraseData.lhTotalNotes,
                rhTotalNotes: phraseData.rhTotalNotes,
                errorScore: phraseData.errorScore,
                lhErrorScore: phraseData.lhErrorScore,
                rhErrorScore: phraseData.rhErrorScore,
                phraseS3Id: phrase?.uuid,
                repertoireId: repertoireIdSelect,
                bpm: phraseData.bpm,
                playSessionId: localPlaySessionId,
              }) as unknown,
            );
          }
        }, 2000);
      }
    },
    [midiStream, localPlaySessionId],
  );

  const startMidiStreamUpdateLoop = useCallback(
    function startMidiStreamUpdateLoop() {
      scheduler2?.setIntervalByTick(
        'updateMidiStream',
        (ev: typeof TickNoteEvent) => {
          /* eslint-disable-next-line no-constant-condition -- inPerformanceMode check placeholder */
          if (true) {
            //!ev.detail.workerState.inPerformanceMode) {
            midiStream.update();
            if (localPlaySessionId) {
              dispatchPhraseEvent(repertoireIdSelect, ev.detail.workerState.play_session_id, 1);
            }
          }
        },
        NoteType._32nd,
        1,
      );
    },
    [midiStream, scheduler2, localPlaySessionId, repertoireIdSelect],
  ); //, inPerformanceMode])

  useEffect(() => {
    if (localPlaySessionId) {
      scheduler2?.updateWorkerState({ play_session_id: localPlaySessionId });
    }
  }, [localPlaySessionId]);

  const startAccuracyLoop = useCallback(() => {
    // logger.info("startAccuracyLoop:", scheduler2)
    // scheduler2?.setTimeoutByTick('setAccuracyIntervalTimeout', ()=> {
    scheduler2?.setIntervalByTick(
      'accuracyLoop',
      function accuracyLoop(_ev: typeof TickNoteEvent) {
        const [accuracy, accuracyAtTempo] = midiStream.calcRepAccuracy(tempo, performanceTempo);
        setAccuracyAtTempo(accuracyAtTempo);
        if (!isNaN(accuracy)) {
          setAccuracy(accuracy);
        }
        // every beat we check eligibility. If it's less than 95 then nope. This is reset every phrase (4 measures)
        // if accuracy dips below .95 at any point, the current phrase is not eligible for a chevron
        // However, we don't count the very first note.
        if (!isNaN(accuracy) && accuracy < 95) {
          //} && midiStream.accuracyQueueLength() >= 1) {
          scheduler2?.updateWorkerState({ isEligible: false });
        }
      },
      divisionsPerMeasureToNoteTypeIn4Over4(timeKeeper.getTimeSignature()?.denominator),
      1,
      0,
    );
    // }, NoteType.WHOLE, 4)
  }, [midiStream, scheduler2, setAccuracy, setAccuracyAtTempo, timeKeeper]);

  useEffect(() => {
    scheduler2?.updateWorkerState({ tempo: tempo });
    if (tempo < minTempoForChevronFlash) {
      scheduler2?.updateWorkerState({ isEligible: false });
      setAllowChevrons(false);
    }
  }, [scheduler2, setAllowChevrons, tempo]);

  const onStop = useCallback(
    (goBackOnPause: boolean = true) => {
      recordPlayTimeIntervalTimer?.current?.pause();
      recordPlayTimeIntervalTimer.current = undefined;
      sendPlayTimeIntervalTimer?.current?.pause();
      sendPlayTimeIntervalTimer.current = undefined;
      clearTimeout(lastPlayedNoteTimer.current);
      lastPlayedNoteTimer.current = undefined;
      scheduler2?.pauseTicks(goBackOnPause);
      setIsPlaying(false);
      if (localPlaySessionId && repertoireReducerData.repertoirePlaying) {
        logger.info(2);
        dispatch(
          eventActions.repertoirePauseEventAction(
            repertoireIdSelect,
            localPlaySessionId,
          ) as unknown,
        );
      }
    },
    [scheduler2],
  );

  const onStart = useCallback(async () => {
    if (!started) {
      await scheduler2?.init({ tempo: tempo });
      // this has to be initialized after the page is loaded and the user has made an action on the screen
      if (errorRecognitionActive) {
        startMidiStreamUpdateLoop();
      }
      startAccuracyLoop();
      setStarted(true);
    }
    if (!recordPlayTimeIntervalTimer?.current) {
      // set last measted time ref on play in order to zero out the time spent during pause.
      lastMeasuredTimeRef.current = new Date().getTime();
      recordPlayTimeIntervalTimer.current = new IntervalTimer(recordPlayTimeInterval, 1000);
      recordPlayTimeIntervalTimer?.current?.start();
    } else {
      // after refactoring to create a new interval function with a new start time after each pause, this really shouldn't run,
      // but I'm going to keep the code anyway just in case.
      recordPlayTimeIntervalTimer?.current?.resume();
    }
    if (!sendPlayTimeIntervalTimer?.current) {
      // set last measted time ref on play in order to zero out the time spent during pause.
      sendPlayTimeIntervalTimer.current = new IntervalTimer(sendPlayTimeInterval, 10000);
      sendPlayTimeIntervalTimer?.current?.start();
    } else {
      // after refactoring to create a new interval function with a new start time after each pause, this really shouldn't run,
      // but I'm going to keep the code anyway just in case.
      sendPlayTimeIntervalTimer?.current?.resume();
    }
    if (process.env.REACT_APP_CURSOR_TIMEOUT) {
      clearTimeout(lastPlayedNoteTimer.current);
      lastPlayedNoteTimer.current = setInterval(() => {
        dispatch(repertoireActions.setRepertoirePlaying({ repertoirePlaying: false }));
        if (localPlaySessionId && repertoireReducerData.repertoirePlaying) {
          logger.info(3);
          dispatch(
            eventActions.repertoirePauseEventAction(
              repertoireIdSelect,
              localPlaySessionId,
            ) as unknown,
          );
        }
      }, 10000);
    }
    if (deferSchedulerStart) {
      // OSMD repertoire path: store the unpause args so startScheduler()
      // can call unpauseTicks later when the first MIDI note is played.
      deferredUnpauseArgsRef.current = {
        tempo,
        tickMultiplier: 1,
        lastPlayedMeasure: lastPlayedMeasureRef.current + 1,
        tickNoteType,
      };
    } else {
      // SVG repertoire path: start the scheduler immediately (existing behavior)
      await scheduler2?.unpauseTicks(tempo, 1, lastPlayedMeasureRef.current + 1, tickNoteType);

      // Apply pending timestamps after TimeKeeper is properly initialized
      // This ensures the cursor shows at the correct count-in position
      if (pendingTopTimestampRef.current !== null) {
        setTopPhraseStartTimestamp(pendingTopTimestampRef.current);
        pendingTopTimestampRef.current = null;
      }
      if (pendingBottomTimestampRef.current !== null) {
        setBottomPhraseStartTimestamp(pendingBottomTimestampRef.current);
        pendingBottomTimestampRef.current = null;
      }

      // Re-apply user's cursor visibility preference (persisted) instead of forcing true
      dispatch(repertoireActions.setCursorVisible(repertoireReducerData.cursorVisible));
    }
  }, [
    onStop,
    scheduler2,
    repertoireReducerData.cursorVisible,
    startAccuracyLoop,
    startMidiStreamUpdateLoop,
    started,
    tempo,
    tickNoteType,
    timeKeeper,
    errorRecognitionActive,
    deferSchedulerStart,
  ]);

  /**
   * Called by the OSMD container when the Phaser scene emits StartedPlaying
   * (i.e., on the first MIDI note). Performs the deferred unpauseTicks call
   * that was skipped in onStart when deferSchedulerStart was true.
   */
  const startScheduler = useCallback(
    async (measureOffset?: number, tempoOverride?: number) => {
      const args = deferredUnpauseArgsRef.current;
      logger.debug('[OSMD-MIDI] Context startScheduler() called', {
        hasDeferredArgs: args !== null,
        schedulerExists: scheduler2 !== null,
        timeKeeperPausedBefore: timeKeeper.isPaused(),
        measureOffset,
        tempoOverride,
      });
      if (!args) {
        logger.warn('[RepertoirePlayContext] startScheduler called but no deferred args stored');
        return;
      }
      deferredUnpauseArgsRef.current = null;

      // When measureOffset is provided (from the Phaser scene's StartedPlaying),
      // override scheduler2.lastPauseMeasure so that unpauseTicks passes the
      // correct startMeasure to timeKeeper.unpause(). Without this, the
      // scheduler's lastPauseMeasure (a negative count-in value from construction)
      // causes the TimeKeeper to produce negative timestamps that miss all
      // event buckets in the event stream.
      if (measureOffset !== undefined && scheduler2) {
        logger.debug('[OSMD-MIDI] Context startScheduler() — overriding lastPauseMeasure', {
          oldValue: scheduler2.lastPauseMeasure,
          newValue: measureOffset,
        });
        scheduler2.lastPauseMeasure = measureOffset;
      }

      // Use tempoOverride if provided — the caller (OSMD container) reads the
      // latest user-chosen tempo from a ref that is always current, whereas
      // args.tempo may be stale if React effects haven't flushed the latest
      // slider value into deferredUnpauseArgsRef yet. This ensures the native
      // scheduler's startPlayback() receives the correct BPM on the very first
      // call, avoiding a brief window of wrong-tempo metronome audio.
      const effectiveTempo = tempoOverride ?? args.tempo;

      await scheduler2?.unpauseTicks(
        effectiveTempo,
        args.tickMultiplier,
        args.lastPlayedMeasure,
        args.tickNoteType,
      );
      logger.debug('[OSMD-MIDI] Context startScheduler() — after unpauseTicks', {
        timeKeeperPausedAfter: timeKeeper.isPaused(),
        lastPauseMeasure: scheduler2?.lastPauseMeasure,
      });

      // Apply pending timestamps after TimeKeeper is properly initialized
      if (pendingTopTimestampRef.current !== null) {
        setTopPhraseStartTimestamp(pendingTopTimestampRef.current);
        pendingTopTimestampRef.current = null;
      }
      if (pendingBottomTimestampRef.current !== null) {
        setBottomPhraseStartTimestamp(pendingBottomTimestampRef.current);
        pendingBottomTimestampRef.current = null;
      }

      // Re-apply user's cursor visibility preference (persisted) instead of forcing true
      dispatch(repertoireActions.setCursorVisible(repertoireReducerData.cursorVisible));
    },
    [scheduler2, dispatch, repertoireReducerData.cursorVisible],
  );

  // This will handle reaching the end and stopping fully
  const onCalcTheEnd = useCallback(async () => {
    if (timeKeeper.getTimeSignature()) {
      const totalNumOfMeasures = musicXMLStream.totalMeasures;
      scheduler2?.setTimeoutByTick(
        'endRepTimeout',
        () => {
          sendPlayTimeInterval();
          dispatch(repertoireActions.setRepertoirePlaying({ repertoirePlaying: false }));
          if (localPlaySessionId) {
            dispatch(
              eventActions.repertoirePauseEventAction(
                repertoireIdSelect,
                localPlaySessionId,
              ) as unknown,
            );
          }

          // When the OSMD container is active, it dispatches RepertoireCompletedEvent
          // using osmdEventStream. Skip the context's own dispatch because midiStream
          // has zero events in OSMD mode and calcRepAccuracy() returns NaN.
          if (!skipContextEventDispatchingRef.current) {
            const [accuracy, accuracyAtTempo] = midiStream.calcRepAccuracy(tempo, performanceTempo);
            const completion = calcCompletion(accuracyAtTempo) * 100;
            logger.info('[ACC-TRACE] CONTEXT onCalcTheEnd DISPATCHING RepertoireCompleted', {
              source: 'context-completed',
              accuracy,
              isNaN_accuracy: isNaN(accuracy),
              typeof_accuracy: typeof accuracy,
              completion,
              midiStreamId: midiStream.id,
              accuracyQueueLength: midiStream.accuracyQueueLength(),
              totalNotesLength: midiStream.totalNotesLength(),
              repertoireIdSelect,
              localPlaySessionId,
            });
            if (repertoireIdSelect && localPlaySessionId) {
              dispatch(
                eventActions.repertoireCompletedEventAction(
                  repertoireIdSelect,
                  localPlaySessionId,
                  completion,
                  accuracy,
                ) as unknown,
              );
            }
          } else {
            logger.info('[ACC-TRACE] CONTEXT onCalcTheEnd SKIPPED (OSMD mode)');
          }
          logger.info('local play session id at end', localPlaySessionId);
          if (localPlaySessionId) {
            logger.info('sending phrase event on end');
            dispatchPhraseEvent(repertoireIdSelect, localPlaySessionId, 0);
          }
          onStop(false); // <- fix this, so it stops and doesn't bounce back if it's stopping for being at the end/
          // reset()
          dispatch(repertoireActions.setRepertoireEnded(true));
          // Need to fix this so that when resets, the error markings are also reset!!!
          // Also need to move setScrollPosition into context, and take out of passing props!
        },
        divisionsPerMeasureToNoteTypeIn4Over4(timeKeeper.getTimeSignature()?.denominator),
        timeKeeper.getTimeSignature().numerator * totalNumOfMeasures - 1,
      );
    }
  }, [
    timeKeeper,
    musicXMLStream,
    repertoireReducerData.repertoirePlaying,
    calcCompletion,
    localPlaySessionId,
  ]);

  useEffect(() => {
    nextPhraseStartTimestampRef.current = 0; // Reset to default value on component mount
    nextPhraseStartMeasureRef.current = 0; // Other related refs can be reset here
    // You might also reset other state or refs as necessary
  }, []);

  useEffect(() => {
    if (timeKeeper.getTimeSignature() && repertoireReducerData.repertoirePlaying) {
      onCalcTheEnd();
    }
  }, [timeKeeper.getTimeSignature(), repertoireReducerData.repertoirePlaying]);

  useEffect(() => {
    if (data.playOnStart && allPhrasesLoaded) {
      setTimeout(() => {
        // not necessary, it can play after allPhrasesLoaded, but gives user a moment to breathe...
        dispatch(repertoireActions.setRepertoirePlaying({ repertoirePlaying: true }));
        if (localPlaySessionId) {
          dispatch(
            eventActions.repertoirePlayEventAction(
              repertoireIdSelect,
              localPlaySessionId,
            ) as unknown,
          );
        }
        dispatch(appActions.setPlayOnStart(false));
      }, 400);
    }
  }, [data.playOnStart, allPhrasesLoaded]);

  useEffect(() => {
    if (initialized && repertoireReducerData.repertoirePlaying) {
      onStart();
    } else {
      onStop();
    }
  }, [repertoireReducerData.repertoirePlaying]);

  useEffect(() => {
    timeKeeper.setBpm(tempo);
    // Keep deferred unpause args in sync so that startScheduler() (called on
    // the first MIDI note after play-press) uses the latest user-chosen BPM
    // instead of the stale value captured at play-press time.
    if (deferredUnpauseArgsRef.current) {
      deferredUnpauseArgsRef.current.tempo = tempo;
    }
  }, [tempo]);

  const _reset = useCallback(async () => {
    timeKeeper.resetTime();
    for (let i = 0; i < phrasePartsRef.current.size; i++) {
      phrasePartsRef.current.set(i, undefined);
    }
    setTopPhraseStartTimestamp(100000);
    setBottomPhraseStartTimestamp(100000);
    resetScheduler();
    clearTimeout(lastPlayedNoteTimer?.current);
    lastPlayedNoteTimer.current = undefined;
    recordPlayTimeIntervalTimer.current?.pause();
    recordPlayTimeIntervalTimer.current?.clear();
    recordPlayTimeIntervalTimer.current = undefined;
    sendPlayTimeIntervalTimer.current?.pause();
    sendPlayTimeIntervalTimer.current?.clear();
    sendPlayTimeIntervalTimer.current = undefined;
    setAllowChevrons(false);
    // setAccuracy(null);  // Reset is called at end, don't reset accuracy yet.
    lastPlayedMeasureRef.current = -1;
    lastUpdatedIndexRef.current = -1;
    nextPhraseStartTimestampRef.current = 0;
    nextPhraseUpdateMeasureRef.current = measuresPerPhrase * 1.5 - 1;
    nextPhraseStartMeasureRef.current = 0;
    midiStream.reset();
    musicXMLStream.reset();

    setStarted(false);
  }, [
    setAllowChevrons,
    setAccuracy,
    dispatch,
    phrasePartsRef,
    midiStream,
    scheduler2,
    getNextNMeasures,
  ]);

  /**
   * Creates a new play session for replay without triggering re-initialization.
   * This is used by softRefreshRepertoire to get a new session ID without re-fetching phrases.
   */
  const startReplaySession = React.useCallback(async () => {
    const jwtToken = (await getCurrentAuthToken()) || '';
    logger.debug('startReplaySession: Creating new session for replay');
    const body = {
      repertoire: {
        id: repertoireIdSelect,
      },
      error_recognition_mode: 'MIDI',
    };
    try {
      const playSession = await postSession(jwtToken, body);
      logger.info('startReplaySession: New session created:', playSession.data.id);
      setLocalPlaySessionId(playSession.data.id);
      // NOTE: We intentionally do NOT set initialized to false here
      // This is the key difference from startNewSession - we don't want to trigger re-init
      dispatch(appActions.invalidateDashboardCache());
      dispatch(
        eventActions.repertoireStartedEventAction(
          repertoireIdSelect,
          playSession.data.id,
        ) as unknown,
      );
    } catch (err) {
      console.error('startReplaySession error:', err);
      if (err instanceof AxiosError && err?.response?.status && err.response.status > 499) {
        awsRum?.recordError(err);
      } else if (!(err instanceof AxiosError)) {
        awsRum?.recordError(err);
      }
    }
  }, [repertoireIdSelect, dispatch]);

  /**
   * Soft reset for replay functionality.
   * Resets the TimeKeeper, Scheduler, and EventStream to their initial state
   * without re-fetching phrases from the server.
   * Creates a new play session ID for tracking.
   */
  const softRefreshRepertoire = useCallback(async () => {
    logger.info('softRefreshRepertoire: Starting soft reset for replay');

    // Save user's preference before hiding (we'll restore it after reset).
    // Dispatching false gets persisted and would overwrite the user's choice.
    const savedCursorVisible = repertoireReducerData.cursorVisible;

    // Hide cursor immediately to prevent flash during reset
    dispatch(repertoireActions.setCursorVisible(false));

    // Stop any current playback
    dispatch(repertoireActions.setRepertoirePlaying({ repertoirePlaying: false }));
    dispatch(repertoireActions.setRepertoireEnded(false));

    // Stop any running timers
    clearTimeout(lastPlayedNoteTimer?.current);
    lastPlayedNoteTimer.current = undefined;
    recordPlayTimeIntervalTimer.current?.pause();
    recordPlayTimeIntervalTimer.current?.clear();
    recordPlayTimeIntervalTimer.current = undefined;
    sendPlayTimeIntervalTimer.current?.pause();
    sendPlayTimeIntervalTimer.current?.clear();
    sendPlayTimeIntervalTimer.current = undefined;
    accumulatedTimeRef.current = 0;
    lastMeasuredTimeRef.current = 0;

    // Get the current phrases before resetting
    const currentPhrases = phrasePartsRef.current
      .toArray()
      .filter((phrase): phrase is Phrase => phrase !== undefined);

    if (currentPhrases.length === 0) {
      logger.warn('softRefreshRepertoire: No phrases available for replay');
      return;
    }

    const firstPhrase = currentPhrases[0];

    // IMPORTANT: Clear note colors on DOM elements BEFORE resetting event stream
    // This iterates through existing event buckets and resets each note's graphics to black
    midiStream.clearErrors();

    // Reset each phrase's play data (clears accuracy tracking, midi events, etc.)
    currentPhrases.forEach((phrase) => {
      phrase.resetPlayData();
    });

    // Reset the EventStream (clears event buckets, accuracy queues)
    midiStream.reset();

    // Calculate count-in measures for the scheduler
    const countInMeasures =
      (firstPhrase.getTimeSigFract() + firstPhrase.getPickUpMeasureOffset()) *
      (1 / firstPhrase.getTimeSigFract());

    // Reset the existing scheduler in place (keeps all references valid)
    // This is critical - creating a new scheduler would cause timing issues
    // because onStart and other callbacks have closure references to scheduler2
    if (scheduler2) {
      scheduler2.pauseTicks(false);
      scheduler2.resetForReplay(-countInMeasures, firstPhrase.getPickUpMeasureOffset());
      // Note: We don't clear pause/unpause callbacks - they should stay registered
    } else {
      // Fallback: create a new scheduler if one doesn't exist
      const newScheduler = new Scheduler2(
        timeKeeper,
        -countInMeasures,
        firstPhrase.getPickUpMeasureOffset(),
      );
      setScheduler2(newScheduler);
    }

    // Re-add events from phrases to the event stream
    let aggLength = 0;
    currentPhrases.forEach((phrase, index) => {
      const numMeasures = phrase.musicXML.measures.length;
      const timeSignatureNumerator = phrase.musicXML.timeSignatures[0]?.numerator;
      const timeSignatureDenominator = phrase.musicXML.timeSignatures[0]?.denominator;

      // Set time signatures on the timeKeeper
      timeKeeper.setTimeSignatures(aggLength, phrase.musicXML.timeSignatures);

      // Re-set the start timestamp on the phrase
      if (index !== 0) {
        phrase.setStartTimestamp(aggLength - firstPhrase.getPickUpMeasureOffset());
      } else {
        phrase.setStartTimestamp(aggLength);
      }

      // Add events to the midiStream using the phrase's iterator
      if (index === 0) {
        midiStream.addEvents2(phrase.getTimesigIterator(), aggLength);
      } else {
        midiStream.addEvents2(
          phrase.getIterator(),
          aggLength - firstPhrase.getPickUpMeasureOffset(),
        );
      }

      aggLength += (numMeasures * timeSignatureNumerator) / timeSignatureDenominator;
    });

    // Reset UI state refs
    lastPlayedMeasureRef.current = -1;
    lastUpdatedIndexRef.current = -1;
    nextPhraseStartTimestampRef.current = 0;
    nextPhraseUpdateMeasureRef.current = measuresPerPhrase * 1.5 - 1;
    nextPhraseStartMeasureRef.current = 0;

    // Reset phrase timestamps for the UI (cursor positioning)
    // Calculate the second phrase start timestamp before the state updates
    const secondPhraseStartTimestamp =
      (currentPhrases[0].musicXML.measures.length *
        currentPhrases[0].musicXML.timeSignatures[0]?.numerator) /
      currentPhrases[0].musicXML.timeSignatures[0]?.denominator;

    // Store the correct timestamps in refs - they will be applied in onStart
    // after unpauseTicks has properly initialized the TimeKeeper
    // This prevents the cursor from flashing at measure 0 before the count-in
    pendingTopTimestampRef.current = 0;
    pendingBottomTimestampRef.current = secondPhraseStartTimestamp;

    // Hide the cursors by setting timestamps to 100000 (initial/hidden value)
    // They will be shown when onStart applies the pending values
    setTopPhraseStartTimestamp(100000);
    setBottomPhraseStartTimestamp(100000);

    // Reset accuracy and chevrons
    setAccuracy(null);
    setAllowChevrons(false);
    setShowAccuracy(false);

    // Reset started state so loops can be re-initialized
    setStarted(false);

    // Increment updateKey to trigger re-renders in child components (e.g., Staff)
    setUpdateKey((prevKey) => prevKey + 1);

    // Create a new play session for replay (without triggering re-initialization)
    await startReplaySession();

    // Restore user's cursor preference so it persists correctly
    dispatch(repertoireActions.setCursorVisible(savedCursorVisible));

    logger.info('softRefreshRepertoire: Soft reset complete, new session created');
  }, [
    dispatch,
    repertoireReducerData.cursorVisible,
    timeKeeper,
    midiStream,
    scheduler2,
    phrasePartsRef,
    measuresPerPhrase,
    setAccuracy,
    setAllowChevrons,
    startReplaySession,
  ]);

  const init = useCallback(async () => {
    // logger.info("About to run this shit?", initialized, auth.jwtToken, "hmm")
    logger.debug('[OSMD-MIDI] Context init() — clearListeners() then addListener()', {
      forwardMidiToPhaser: forwardMidiToPhaserRef.current,
      midiStreamId: midiStream.id,
      listenerName: listener.current.name,
    });
    clearListeners();
    addListener(listener.current);
    const authToken = await auth.currentAuthToken();
    if (!initialized && authToken && repertoireIdSelect) {
      // make sure level is not in a playing state (for pause button)
      dispatch(repertoireActions.setRepertoirePlaying({ repertoirePlaying: false }));

      // Initialize the first top and bottom phrases
      if (await auth.currentAuthToken()) {
        if (musicXMLStream.length() === 0) {
          console.error('[MUS-145-DBG] Context init() — calling getNextNMeasures()');
          try {
            await Promise.all([getNextNMeasures()]);
            console.error(
              '[MUS-145-DBG] Context init() — getNextNMeasures() done, setting allPhrasesLoaded=true',
            );
            setAllPhrasesLoaded(true);
          } catch (error) {
            console.error('[MUS-145-DBG] Context init() — getNextNMeasures() FAILED:', error);
          }
        } else {
          console.error(
            '[MUS-145-DBG] Context init() — musicXMLStream already has data, length:',
            musicXMLStream.length(),
          );
        }
      } else {
        console.error('[MUS-145-DBG] Context init() — no jwt token available');
      }
      setInitialized(true);
      console.error('[MUS-145-DBG] Context init() — initialized=true');
    }
  }, [
    dispatch,
    getTiersForLevel,
    initialized,
    scheduler2,
    updatePhraseParts,
    auth.currentAuthToken,
    repertoireIdSelect,
  ]);

  // ---- Reset context state when the selected piece changes ----
  // Previously the container was remounted (via key prop) on piece change,
  // which unmounted and remounted the context implicitly. Now the container
  // stays alive, so we reset the context state here to allow init() to
  // re-fetch phrases for the new piece.
  const prevRepertoireIdRef = useRef(repertoireIdSelect);
  useEffect(() => {
    if (prevRepertoireIdRef.current !== repertoireIdSelect && repertoireIdSelect) {
      console.error(
        '[MUS-145-DBG] Context — repertoireIdSelect changed from',
        prevRepertoireIdRef.current,
        'to',
        repertoireIdSelect,
        '— resetting state',
      );
      prevRepertoireIdRef.current = repertoireIdSelect;

      // Reset state so init() can re-run for the new piece
      setInitialized(false);
      setAllPhrasesLoaded(false);
      setMusicLoaded(false);
      setPhraseParts(List([]));
      phrasePartsRef.current = List([]);
      musicXMLStream.reset();
      midiStream.reset();
      timeKeeper.resetTime();
      setStarted(false);

      // Stop any active playback
      dispatch(repertoireActions.setRepertoirePlaying({ repertoirePlaying: false }));
    }
  }, [repertoireIdSelect]);

  useEffect(() => {
    console.error(
      '[MUS-145-DBG] Context init effect — isAuthenticated:',
      auth.isAuthenticated,
      'levelDataLength:',
      data.levelData.length,
      'repertoireIdSelect:',
      repertoireIdSelect,
      'localPlaySessionId:',
      !!localPlaySessionId,
    );
    if (
      auth.isAuthenticated &&
      data.levelData.length > 0 &&
      repertoireIdSelect &&
      localPlaySessionId
    ) {
      // don't init context until level data is loaded (could also wait til on repertoire play page or something..)
      console.error('[MUS-145-DBG] Context init effect — calling init()');
      init();
    }
  }, [
    auth.isAuthenticated,
    data.levelData.length,
    repertoireReducerData.repertoireIdSelect,
    localPlaySessionId,
  ]);

  useComponentWillUnmount(() => {
    console.error('[MUS-145-DBG] Context unmounting');
    removeListener(listener.current);
    if (keyCallbackRef?.current) {
      document.removeEventListener('keyup', keyCallbackRef?.current);
    }
    setInitialized(false);
    onStop();
    dispatch(repertoireActions.setRepertoirePlaying({ repertoirePlaying: false }));
    // midiValInput?.disconnect()
    midiStream.reset();
    musicXMLStream.reset();
    scheduler2?.reset();
    scheduler2?.cleatPauseUnpauseCallbacks();
    setScheduler2(null);
  });

  React.useEffect(() => {
    if (playSessionId !== null && !localPlaySessionId) {
      console.log('setting play session to null');
      dispatch(repertoireActions.setPlaySessionId(null));
    }
  }, [playSessionId]);

  const startNewSession = React.useCallback(async () => {
    // const user = await Auth.currentAuthenticatedUser();
    // const user = await getCurrentUser();
    const jwtToken = (await getCurrentAuthToken()) || '';
    logger.debug('jwtToken: startNewSession', jwtToken);
    const body = {
      repertoire: {
        id: repertoireIdSelect,
      },
      error_recognition_mode: 'MIDI',
    };
    try {
      // const playSession = await postSession(user?.signInUserSession?.accessToken?.jwtToken, body)
      const playSession = await postSession(jwtToken, body);

      console.log('play session created: ', playSession.data.id);
      setLocalPlaySessionId(playSession.data.id);
      setInitialized(false);
      console.log('repertoire id', repertoireIdSelect);
      console.log('seding play session started event with', playSession.data.id);
      console.log('setting local play session id to ', playSession.data.id);
      dispatch(appActions.invalidateDashboardCache());
      dispatch(
        eventActions.repertoireStartedEventAction(
          repertoireIdSelect,
          playSession.data.id,
        ) as unknown,
      );
      logger.debug('play session created 2', playSession.data.id);
      // yield put(repActions.setPlaySessionId(playSession.data.id))
    } catch (err) {
      console.error(err);
      if (err instanceof AxiosError && err?.response?.status && err.response.status > 499) {
        awsRum?.recordError(err);
      } else if (!(err instanceof AxiosError)) {
        awsRum?.recordError(err);
      }
    }
    // createRepSession(repertoireIdSelect).then(newID => {
    //   console.log("repertoire id", repertoireIdSelect)
    //   console.log("seding play session started event with", playSessionId)
    //   console.log("setting local play session id to ", newID)
    //   setLocalPlaySessionId(newID)
    //   setInitialized(false)
    //   dispatch(eventActions.repertoireStartedEventAction(repertoireIdSelect, newID));
    // })
  }, [repertoireIdSelect, localPlaySessionId]);

  React.useEffect(() => {
    console.log('checking to start new session', localPlaySessionId);
    if (!localPlaySessionId) {
      console.log('starting new session');
      startNewSession();
    }
  }, [localPlaySessionId]);

  // React.useEffect(() => {
  //   console.log("playSessionId",playSessionId)
  //   console.log("localPlaySessionId",localPlaySessionId)
  //   if(playSessionId !== localPlaySessionId && playSessionId !== null && repertoireIdSelect) {
  //     repertoireActions.setPlaySessionId(null)
  //     setInitialized(false)
  //     setLocalPlaySessionId(playSessionId)
  //     console.log("repertoire id", repertoireIdSelect)
  //     console.log("seding play session started event with", playSessionId)
  //     dispatch(eventActions.repertoireStartedEventAction(repertoireIdSelect, playSessionId));
  //   }
  // },[playSessionId,localPlaySessionId])

  // React.useEffect(() => {
  //   if(!localPlaySessionId && playSessionId && repertoireIdSelect) {
  //       console.log("repertoire id", repertoireIdSelect)
  //       console.log("seding play session started event with", localPlaySessionId)
  //       dispatch(eventActions.repertoireStartedEventAction(repertoireIdSelect, localPlaySessionId));
  //   }
  // },[localPlaySessionId])

  return (
    <RepertoirePlayContext.Provider
      value={{
        ...state,
        allowChevrons: allowChevrons,
        accuracyPercent: accuracyPercent,
        scheduler2: scheduler2,
        midiStream: midiStream,
        topPhraseStartTimestamp: topPhraseStartTimestamp,
        bottomPhraseStartTimestamp: bottomPhraseStartTimestamp,
        performanceTempo: performanceTempo,
        inPerformanceMode: inPerformanceMode,
        musicLoaded: musicLoaded,
        phraseParts: phraseParts,
        updateKey: updateKey,
        allPhrasesLoaded: allPhrasesLoaded,
        isPlaying,
        setIsPlaying,

        showCursorSelector: showCursorSelector,
        setShowCursorSelector: setShowCursorSelector,
        setInPerformanceMode: setInPerformanceMode,
        createScheduler: createScheduler,

        setAccuracy,
        setAllowChevrons,
        setMusicLoaded,
        softRefreshRepertoire,
        deferSchedulerStart,
        setDeferSchedulerStart,
        startScheduler,
        setForwardMidiToPhaser,
        setSkipContextEventDispatching,
        localPlaySessionId,
      }}
    >
      {children}
    </RepertoirePlayContext.Provider>
  );
};
function _usePrevious(_localPlaySessionId: string | null | undefined) {
  throw new Error('Function not implemented.');
}
